import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Target, Briefcase, ShieldCheck, BookOpen, Brain, Sparkles,
  ChevronRight, CheckCircle2, Users, Star, MessageSquare,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { API } from "../context/AuthContext";

const IMG = (p) => `${API}/static/illustrations/${p}`;

const Stat = ({ value, label }) => (
  <div className="text-center">
    <div className="font-display text-4xl md:text-5xl font-bold text-[#0A2518]">{value}</div>
    <div className="mt-1 text-sm text-[#4B5563]">{label}</div>
  </div>
);

const Pillar = ({ icon: Icon, title, text, delay }) => (
  <div className={`fade-up ${delay} card-hover bg-white border border-[#E5EBE7] rounded-2xl p-8`}>
    <div className="inline-flex items-center justify-center h-14 w-14 rounded-xl bg-[#F0FDF4] ring-1 ring-[#16A34A]/20 mb-5">
      <Icon className="h-7 w-7 text-[#16A34A]" />
    </div>
    <h3 className="font-display text-2xl font-semibold text-[#0A2518] mb-2">{title}</h3>
    <p className="text-[#4B5563] leading-relaxed">{text}</p>
  </div>
);

const Step = ({ n, title, text }) => (
  <div className="relative pl-14">
    <div className="absolute left-0 top-0 inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#16A34A] text-white font-display font-semibold">
      {n}
    </div>
    <h4 className="font-display text-xl font-semibold text-[#0A2518] mb-1">{title}</h4>
    <p className="text-[#4B5563] leading-relaxed">{text}</p>
  </div>
);

export default function Home() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({ educators: 0, parents: 0, matches: 0 });

  useEffect(() => {
    axios.get(`${API}/stats`).then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <span className="geo-shape bg-[#16A34A]/25 h-72 w-72 -top-20 -left-20 drift-slow" aria-hidden />
        <span className="geo-shape bg-[#16A34A]/15 h-96 w-96 top-40 -right-32 drift-slow" aria-hidden />
        <span className="geo-ring h-[28rem] w-[28rem] -top-32 right-1/4" aria-hidden />
        <span className="geo-square h-40 w-40 bottom-10 left-10 opacity-60" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 lg:pt-28 pb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="pill fade-up" data-testid="home-hero-pill">
                <Sparkles className="h-3.5 w-3.5" /> {t("home.heroPill")}
              </span>
              <h1 className="fade-up delay-100 mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight text-[#0A2518] leading-[1.02]">
                Mon<span className="text-[#16A34A]">Repetiteur</span>
              </h1>
              <p className="fade-up delay-200 mt-6 text-xl sm:text-2xl text-[#4B5563] font-light leading-relaxed max-w-xl">
                {t("home.heroSubtitle")} <em className="text-[#15803D] not-italic font-medium">{t("home.heroSubtitleEm")}</em>.
              </p>
              <p className="fade-up delay-300 mt-4 text-base text-[#4B5563] max-w-xl leading-relaxed">
                {t("home.heroIntro")}
              </p>

              <div className="fade-up delay-400 mt-9 flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="rounded-full h-14 px-8 bg-[#16A34A] hover:bg-[#15803D] text-white font-medium text-base shadow-lg shadow-[#16A34A]/20" data-testid="home-cta-find-educator">
                  <Link to="/register?role=parent">
                    <Target className="h-5 w-5 mr-2" />
                    {t("home.ctaFindEducator")}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full h-14 px-8 border-2 border-[#16A34A] text-[#16A34A] bg-white hover:bg-[#F0FDF4] font-medium text-base" data-testid="home-cta-become-tutor">
                  <Link to="/register?role=educator">
                    <Briefcase className="h-5 w-5 mr-2" />
                    {t("home.ctaBecomeTutor")}
                  </Link>
                </Button>
              </div>

              <div className="fade-up delay-500 mt-14 grid grid-cols-3 gap-6 max-w-md">
                <Stat value={`${stats.educators}+`} label={t("home.stats.educators")} />
                <Stat value={`${stats.parents}+`} label={t("home.stats.parents")} />
                <Stat value="4.9★" label={t("home.stats.rating")} />
              </div>
            </div>

            <div className="relative fade-up delay-300">
              <div className="relative rounded-3xl overflow-hidden border border-[#E5EBE7] shadow-2xl shadow-[#0A2518]/10">
                <img src={IMG("home_hero.png")} alt="" className="w-full h-[520px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A2518]/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  </div>
                  <p className="font-display text-lg italic leading-snug">{t("home.heroTestimonialQuote")}</p>
                  <p className="text-sm opacity-80 mt-2">{t("home.heroTestimonialAuthor")}</p>
                </div>
              </div>

              <div className="hidden md:flex absolute -left-6 top-8 bg-white rounded-2xl shadow-xl border border-[#E5EBE7] px-5 py-4 items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#F0FDF4]">
                  <Brain className="h-5 w-5 text-[#16A34A]" />
                </span>
                <div>
                  <div className="font-display font-semibold text-sm text-[#0A2518]">{t("home.badgeAIMatching")}</div>
                  <div className="text-xs text-[#4B5563]">{t("home.badgeAIMatchingSub")}</div>
                </div>
              </div>
              <div className="hidden md:flex absolute -right-6 -bottom-6 bg-white rounded-2xl shadow-xl border border-[#E5EBE7] px-5 py-4 items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#F0FDF4]">
                  <ShieldCheck className="h-5 w-5 text-[#16A34A]" />
                </span>
                <div>
                  <div className="font-display font-semibold text-sm text-[#0A2518]">{t("home.badgeVerified")}</div>
                  <div className="text-xs text-[#4B5563]">{t("home.badgeVerifiedSub")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 bg-[#F3F7F5]" data-testid="home-pillars">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="pill"><CheckCircle2 className="h-3.5 w-3.5" /> {t("home.pillars.pill")}</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A2518]">{t("home.pillars.title")}</h2>
            <p className="mt-4 text-[#4B5563] max-w-2xl mx-auto">{t("home.pillars.subtitle")}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Pillar icon={ShieldCheck} title={t("home.pillars.selectionTitle")} text={t("home.pillars.selectionText")} delay="delay-100" />
            <Pillar icon={Target} title={t("home.pillars.tailoredTitle")} text={t("home.pillars.tailoredText")} delay="delay-200" />
            <Pillar icon={BookOpen} title={t("home.pillars.simpleTitle")} text={t("home.pillars.simpleText")} delay="delay-300" />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24" data-testid="home-how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="pill"><Users className="h-3.5 w-3.5" /> {t("home.how.pill")}</span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-[#0A2518]">{t("home.how.title")}</h2>
              <p className="mt-4 text-[#4B5563] leading-relaxed">{t("home.how.intro")}</p>

              <div className="mt-10 space-y-8">
                <Step n="1" title={t("home.how.step1Title")} text={t("home.how.step1Text")} />
                <Step n="2" title={t("home.how.step2Title")} text={t("home.how.step2Text")} />
                <Step n="3" title={t("home.how.step3Title")} text={t("home.how.step3Text")} />
              </div>

              <div className="mt-10">
                <Button asChild className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white h-12 px-7" data-testid="home-cta-start">
                  <Link to="/register?role=parent">
                    {t("home.how.ctaStart")}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden border border-[#E5EBE7]">
                <img src={IMG("home_how.png")} alt="" className="w-full h-[560px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-[#E5EBE7] px-5 py-4 max-w-xs">
                <div className="flex items-center gap-2 text-[#16A34A] mb-1.5">
                  <MessageSquare className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">{t("home.how.testimonialTag")}</span>
                </div>
                <p className="font-display italic text-sm text-[#0A2518] leading-snug">{t("home.how.testimonialQuote")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0A2518] relative overflow-hidden">
        <span className="geo-shape bg-[#16A34A]/30 h-96 w-96 -top-32 -right-20" aria-hidden />
        <span className="geo-shape bg-[#16A34A]/20 h-72 w-72 -bottom-20 -left-20" aria-hidden />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-white">{t("home.finalCta.title")}</h2>
          <p className="mt-4 text-lg text-white/80 max-w-2xl mx-auto">{t("home.finalCta.subtitle")}</p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild size="lg" className="rounded-full h-14 px-8 bg-white text-[#0A2518] hover:bg-[#F0FDF4]" data-testid="home-cta-final-parent">
              <Link to="/register?role=parent">
                <Target className="h-5 w-5 mr-2" />
                {t("home.finalCta.iAmParent")}
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full h-14 px-8 border-2 border-white text-white bg-transparent hover:bg-white hover:text-[#0A2518]" data-testid="home-cta-final-educator">
              <Link to="/register?role=educator">
                <Briefcase className="h-5 w-5 mr-2" />
                {t("home.finalCta.iAmEducator")}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
